/**
 * useDockPins — macOS-style pinned dock items
 * Pinned items persist in localStorage
 * Unpinned open windows show temporarily with a dot
 */
import { useCallback, useEffect, useState } from 'react'
import { DOCK_ITEMS, type DockItemConfig } from './dockConfig'

const STORAGE_KEY = 'runai_dock_pins'

// Default pinned IDs from dockConfig
const DEFAULT_PINS = DOCK_ITEMS.filter(i => !i.separator).map(i => i.id)

export function useDockPins() {
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? JSON.parse(saved) : DEFAULT_PINS
    } catch {
      return DEFAULT_PINS
    }
  })

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pinnedIds))
    } catch { /* non-critical */ }
  }, [pinnedIds])

  const isPinned = useCallback((id: string) => pinnedIds.includes(id), [pinnedIds])

  const pin = useCallback((id: string) => {
    setPinnedIds(prev => prev.includes(id) ? prev : [...prev, id])
  }, [])

  const unpin = useCallback((id: string) => {
    setPinnedIds(prev => prev.filter(p => p !== id))
  }, [])

  const togglePin = useCallback((id: string) => {
    setPinnedIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id])
  }, [])

  // Get ordered pinned items from dockConfig
  const pinnedItems: DockItemConfig[] = pinnedIds
    .map(id => DOCK_ITEMS.find(i => i.id === id))
    .filter((i): i is DockItemConfig => !!i)

  const reorder = useCallback((fromId: string, toId: string) => {
    setPinnedIds(prev => {
      const arr = [...prev]
      const fromIdx = arr.indexOf(fromId)
      const toIdx = arr.indexOf(toId)
      if (fromIdx === -1 || toIdx === -1) return prev
      arr.splice(fromIdx, 1)
      arr.splice(toIdx, 0, fromId)
      return arr
    })
  }, [])

  return { pinnedIds, pinnedItems, isPinned, pin, unpin, togglePin, reorder }
}
